import { SidePanelStatus } from "@nova/component-side-panel-tab-container"
import { ImageProcessingState, JobStatusType } from "@nova/constants"
import { selectVideoColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { selectUploadTimestampForFlightId } from "features/map/layers/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { selectVideoColorFinderJobByFlightId } from "plugins/ColorFinder/VideoColorFinder/ColorFinderSlice"
import { useVideoColorfinderProgress } from "plugins/ColorFinder/VideoColorFinder/preview-files/VideoColorfinderPreviewFiles"
import { processingTimeout, StatusAndProgressData } from "../generics/GenericLayerListItemProgress"
import { selectColorFinderImagesAndUploadsByFlightId } from "features/entity-caching/images/colorfinder/selectors"
import { getSummaryFromUploads } from "../upload-flow/module"
import { useMemo } from "react"

const getVideoColorfinderStatus = (
    jobStatus?: JobStatusType,
    isUploading?: boolean,
    isProcessing?: boolean,
    isDemo?: boolean
) => {
    const isDone =
        jobStatus === JobStatusType.COMPLETED || jobStatus === JobStatusType.MOCKCOMPLETED
    if (isProcessing) return SidePanelStatus.loading
    if (isUploading) return SidePanelStatus.singleFileUploading
    if (!jobStatus) return SidePanelStatus.upload
    if (jobStatus === JobStatusType.QUEUED) {
        if (isDemo) return SidePanelStatus.ready
        else return SidePanelStatus.queued
    }
    if (jobStatus === JobStatusType.STOPPED || jobStatus === JobStatusType.FAILED) return undefined
    if (jobStatus === JobStatusType.STARTED || jobStatus === JobStatusType.LIVE)
        return SidePanelStatus.loading
    if (isDone) return undefined

    return undefined
}

export function useVideoColorfinderStatusAndProgress(id: string): StatusAndProgressData {
    const entity = useAppSelector(selectVideoColorFinderFlightById(id))
    const isDemo = !!entity.demoId
    const job = useAppSelector(selectVideoColorFinderJobByFlightId(id))
    const { uploadProgress, processingProgress } = useVideoColorfinderProgress(id)
    const uploadTimestamp = useAppSelector(selectUploadTimestampForFlightId(id))
    const processingInProgress =
        !!uploadTimestamp && processingProgress < 1 && job?.status !== JobStatusType.MOCKCOMPLETED
    const jobStatus = getVideoColorfinderStatus(
        job?.status,
        !!uploadProgress && uploadProgress < 1,
        processingInProgress,
        isDemo
    )
    const progress = processingInProgress ? Math.round(processingProgress * 100) : uploadProgress
    let createdAtDate: Date | undefined
    if (uploadTimestamp) {
        createdAtDate = new Date(uploadTimestamp)
        if (
            status === SidePanelStatus.loading &&
            new Date().getTime() - createdAtDate.getTime() >= processingTimeout
        ) {
            status = SidePanelStatus.failed
        }
    }
    switch (jobStatus) {
        case SidePanelStatus.loading:
            return {
                status: jobStatus,
                progress,
                createdAtDate,
            }
        case SidePanelStatus.singleFileUploading:
            return {
                status: jobStatus,
                createdAtDate,
                progress,
                totalUploadCount: undefined,
            }
    }
    return {
        status: jobStatus,
    }
}

export function useImageColorfinderStatusAndProgress(id: string): StatusAndProgressData {
    const uploads = useAppSelector(selectColorFinderImagesAndUploadsByFlightId(id))
    const summary = getSummaryFromUploads(uploads)
    const completedUploadCount = summary.stateCount?.[ImageProcessingState.UploadDone]
    const totalUploadCount = summary.uploadCount
    const uploadTimestamp = useAppSelector(selectUploadTimestampForFlightId(id))
    const createdAtDate = uploadTimestamp ? new Date(uploadTimestamp) : undefined
    const isDone = completedUploadCount === totalUploadCount && (totalUploadCount || 0) > 0
    const status = useMemo(
        () =>
            isDone
                ? undefined
                : !totalUploadCount
                  ? SidePanelStatus.empty
                  : completedUploadCount > 0
                    ? SidePanelStatus.uploading
                    : SidePanelStatus.upload,
        [completedUploadCount, isDone, totalUploadCount]
    )
    switch (status) {
        case SidePanelStatus.uploading:
            return {
                status,
                completedUploadCount,
                createdAtDate,
                totalUploadCount,
            }
    }
    return {
        status,
    }
}
